import { BrowserRouter, Routes, Route, useLocation, useNavigationType, Navigate } from "react-router-dom";
import { useEffect, useRef, lazy, Suspense } from "react";
import { AnimatePresence, motion } from "framer-motion";
import "@/App.css";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import SmoothScroll from "@/components/SmoothScroll";
import MagneticCursor from "@/components/MagneticCursor";
import SkipLink from "@/components/SkipLink";
import CommandPalette from "@/components/CommandPalette";
import { LanguageProvider } from "@/i18n/LanguageContext";
import Home from "@/pages/Home";

// Code-split the non-home routes so the initial bundle stays slim.
// Home stays eager because it's the most likely landing page.
const WorksIndex = lazy(() => import("@/pages/WorksIndex"));
const WorkDetail = lazy(() => import("@/pages/WorkDetail"));
const About = lazy(() => import("@/pages/About"));
const Rooms = lazy(() => import("@/pages/Rooms"));
const Acquisitions = lazy(() => import("@/pages/Acquisitions"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Letter = lazy(() => import("@/pages/Letter"));
const NotFound = lazy(() => import("@/pages/NotFound"));

function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType(); // POP = browser back/forward
  const prevPath = useRef(location.pathname);

  // Continuously track home scroll position
  useEffect(() => {
    if (location.pathname !== "/") return;
    let raf = 0;
    const save = () => {
      sessionStorage.setItem("home-scroll", String(window.scrollY));
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(save);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [location.pathname]);

  // On route change: restore (only on browser back/forward) or reset
  useEffect(() => {
    const from = prevPath.current;
    const to = location.pathname;
    prevPath.current = to;

    // Restore stored scroll ONLY when the user came via browser back/forward.
    // Explicit link clicks (PUSH/REPLACE) should always start at the top.
    if (to === "/" && navType === "POP") {
      const stored = sessionStorage.getItem("home-scroll");
      const y = stored ? parseInt(stored, 10) : 0;
      const restore = () => {
        if (window.__lenis) {
          window.__lenis.scrollTo(y, { immediate: true, force: true });
        } else {
          window.scrollTo(0, y);
        }
      };
      requestAnimationFrame(() => requestAnimationFrame(restore));
      setTimeout(restore, 250);
    } else {
      // Leaving home or arriving via explicit click: reset to top.
      if (to === "/") sessionStorage.removeItem("home-scroll");
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
    }
  }, [location.pathname, navType]);

  return null;
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      >
        <Suspense fallback={<div aria-hidden className="min-h-screen bg-bone" />}>
          <Routes location={location}>
            <Route path="/" element={<Home />} />
            <Route path="/works" element={<WorksIndex />} />
            <Route path="/works/untitled-005" element={<Navigate to="/works/i-was-never-cast" replace />} />
            <Route path="/works/untitled-007" element={<Navigate to="/works/everything-that-touched-me" replace />} />
            <Route path="/works/untitled-010" element={<Navigate to="/works/i-was-standing-too-close" replace />} />
            <Route path="/works/untitled-011" element={<Navigate to="/works/i-was-mostly-adding" replace />} />
            <Route path="/works/untitled-012" element={<Navigate to="/works/what-the-stars-said" replace />} />
            <Route path="/works/untitled-2026-03" element={<Navigate to="/works/the-gold-was-always-underneath" replace />} />
            <Route path="/works/untitled-2026-04" element={<Navigate to="/works/we-started-waking" replace />} />
            <Route path="/works/untitled-2026-05" element={<Navigate to="/works/we-started-living" replace />} />
            <Route path="/works/:slug" element={<WorkDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/rooms" element={<Navigate to="/exhibitions" replace />} />
            <Route path="/exhibitions" element={<Rooms />} />
            <Route path="/acquisitions" element={<Acquisitions />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/letter" element={<Navigate to="/contact" replace />} />
            <Route path="/contact" element={<Letter />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <div className="App">
          <div className="grain-overlay" />
          <SkipLink />
          <SmoothScroll />
          <MagneticCursor />
          <ScrollManager />
          <CommandPalette />
          <Nav />
          <main id="main">
            <AnimatedRoutes />
          </main>
          <Footer />
        </div>
      </LanguageProvider>
    </BrowserRouter>
  );
}
